<template>
<section>
  <div v-if="computedLineups.home.length || computedLineups.away.length" class="lineups">
    <template v-for="section in sections">
      <div v-if="section.title" :key="section.key + '-title'" class="lineups__separator"><span>{{ section.title }}</span></div>
      <div :key="section.key" class="lineups__grid">
        <template v-for="(row, idx) in section.rows">
          <div :key="section.key + '-h' + idx" class="lineups__player lineups__player--home">
            <template v-if="row.home">
              <span v-if="row.home.captain" class="captain-badge">C</span>
              <span v-if="row.home.card" class="lineups__card" v-html="cardSvgs[row.home.card]"></span>
              <span v-if="row.home.sub" class="lineups__sub" :title="$t(row.home.sub === 'in' ? 'statistics.subin' : 'statistics.subout')">
                <span v-if="row.home.subMinute" class="lineups__sub-minute">{{ row.home.subMinute }}</span><span class="lineups__sub-icon" v-html="subSvgs[row.home.sub]"></span>
              </span>
              <span v-if="row.home.goals" class="lineups__goals" :title="$t('statistics.playergoals')">
                <img class="lineups__ball" :src="goalIcon" alt=""><span v-if="row.home.goals > 1" class="lineups__goals-count">{{ row.home.goals }}</span>
              </span>
              <span class="lineups__name text-truncate" :title="row.home.name">{{ row.home.short }}</span>
              <LogoCompetitors v-if="row.home.teamId" :team-id="row.home.teamId" height="14px" width="14px" class="player-logo"/>
            </template>
          </div>
          <div :key="section.key + '-a' + idx" class="lineups__player lineups__player--away">
            <template v-if="row.away">
              <LogoCompetitors v-if="row.away.teamId" :team-id="row.away.teamId" height="14px" width="14px" class="player-logo"/>
              <span class="lineups__name text-truncate" :title="row.away.name">{{ row.away.short }}</span>
              <span v-if="row.away.goals" class="lineups__goals" :title="$t('statistics.playergoals')">
                <img class="lineups__ball" :src="goalIcon" alt=""><span v-if="row.away.goals > 1" class="lineups__goals-count">{{ row.away.goals }}</span>
              </span>
              <span v-if="row.away.sub" class="lineups__sub" :title="$t(row.away.sub === 'in' ? 'statistics.subin' : 'statistics.subout')">
                <span class="lineups__sub-icon" v-html="subSvgs[row.away.sub]"></span><span v-if="row.away.subMinute" class="lineups__sub-minute">{{ row.away.subMinute }}</span>
              </span>
              <span v-if="row.away.card" class="lineups__card" v-html="cardSvgs[row.away.card]"></span>
              <span v-if="row.away.captain" class="captain-badge">C</span>
            </template>
          </div>
        </template>
      </div>
    </template>
  </div>
  <div v-else class="empty-state"><v-icon size="40" color="#333">mdi-account-group</v-icon><div class="mt-2">{{ $t('statistics.nolineups') }}</div></div>
</section>
</template>

<script>
import LogoCompetitors from "@/components/center_widgets/LogoCompetitors.vue";
import {collectIncidents, incidentPlayer, typeOf, YELLOW_CARD, RED_CARD, SUBSTITUTION, CARD_SVGS, SUB_SVGS, GOAL_ICON} from "@/services/matchtracker/matchIncidents";
import {inSquad} from "@/services/matchtracker/playerStatsPoller";

// position.id -> place in the list: 3 Goalkeeper, 2 Defender, 1 Midfielder, 4 Forward.
const ROLE_ORDER = { 3: 0, 2: 1, 1: 2, 4: 3 };
// Same order by the position names of the player-stats API.
const ROLE_ORDER_BY_NAME = { Goalkeeper: 0, Defender: 1, Midfielder: 2, Forward: 3 };

export default {
  props: {
    matchData: { type: Object, required: true},
    // live-scores/player-stats answer: when there, it drives the lineups
    // (with goals and cards per player).
    playerStats: { type: Object, default: null }
  },
  name:"LineupSection",
  components: { LogoCompetitors },
  data() {
    return {
      cardSvgs: CARD_SVGS,
      goalIcon: GOAL_ICON,
      subSvgs: SUB_SVGS
    };
  },
  watch: {
  },
  mounted() {
  },
  beforeDestroy() {
  },
  methods: {
    // "red" when sent off (straight red or second yellow), "yellow" when booked:
    // from the player's own stats, else from the livescore incidents.
    cardOf(p) {
      if (p.red || p.yellow >= 2) return 'red';
      if (p.yellow) return 'yellow';
      const byId = p.id !== undefined && p.id !== null ? this.cardsByPlayer.byId[String(p.id)] : null;
      const c = byId || this.cardsByPlayer.byName[String(p.name).trim().toLowerCase()];
      if (!c) return null;
      return c.red || c.yellow >= 2 ? 'red' : 'yellow';
    },
    // "out" for a starter replaced, "in" for a benched player who came on:
    // the substitution incident names one player, the lineup tells which side
    // of the change they were. With the player-stats API a benched player is
    // "in" only when it says they played (hasPlayed).
    subOf(p) {
      const none = { sub: null, subMinute: '' };
      const seconds = this.subsByPlayer[String(p.id)];
      const minute = seconds !== undefined ? `${Math.floor(seconds / 60)}'` : '';
      if (p.starter) return minute ? { sub: 'out', subMinute: minute } : none;
      if (!p.benched) return none;
      if (p.cameOn !== undefined) return p.cameOn ? { sub: 'in', subMinute: minute } : none;
      return minute ? { sub: 'in', subMinute: minute } : none;
    }
  },
  computed: {
    ls() { return this.matchData && (this.matchData.Livescore || this.matchData.livescore) ? (this.matchData.Livescore || this.matchData.livescore) : null },
    isValidData() { return this.ls !== null && (this.ls.Scoreboard || this.ls.scoreboard) !== undefined; },
    // Lineups from the player-stats API: teams[].players, by team side.
    apiLineups() {
      const teams = this.playerStats?.teams || [];
      const side = pos => {
        const team = teams.find(t => t.side === (pos === '1' ? 'home' : 'away') || String(t.position) === pos);
        return (team?.players || []).filter(p => inSquad(p) && String(p.name || '').trim()).map((p, idx) => {
          const stats = p.stats || {};
          return {
            id: p.playerId || idx,
            name: String(p.name).trim(),
            number: p.shirtNumber || '',
            captain: p.isCaptain === true,
            teamId: team.id || '',
            role: ROLE_ORDER_BY_NAME[p.position] ?? 99,
            // The API marks the eleven "Playing" and the subs "Benched" (even
            // once they came on). Came on from the bench: not in the starting
            // lineup, "Benched" and played.
            starter: p.isStartingLineup === true || p.state === 'Playing',
            benched: p.isStartingLineup !== true && p.state === 'Benched',
            cameOn: p.isStartingLineup !== true && p.state === 'Benched' && p.hasPlayed === true,
            goals: parseInt(stats.goals) || 0,
            yellow: parseInt(stats.yellowCards) || 0,
            red: parseInt(stats.redCards) || 0
          };
        }).sort((a, b) => a.role - b.role);
      };
      return { home: side('1'), away: side('2') };
    },
    // Lineups from event-detail: competitors[].players, by competitor position.
    competitorLineups() {
      const comps = this.matchData?.competitors || [];
      // { playerId, shirtNumber, isCaptain, position: {id, name}, player: {id, name, ...} }
      const toPlayer = (p, idx, competitor) => ({
        id: p.playerId || p.player?.id || p.id || idx,
        name: p.player?.name || p.name || p.playerName || 'Unknown',
        number: p.shirtNumber || p.player?.shirtNumber || '',
        captain: p.isCaptain === true,
        // Logo of the player's team (falls back to the competitor's id).
        teamId: p.player?.teamId || p.teamId || competitor?.id || '',
        // Role order: goalkeeper, defenders, midfielders, forwards; unknown last.
        role: ROLE_ORDER[String(p.position?.id)] ?? 99,
        // Starting eleven: isStartingLineup true and not benched (state 1 = "Benched").
        starter: p.isStartingLineup === true && String(p.state?.id) !== '1',
        benched: String(p.state?.id) === '1'
      });
      const side = pos => {
        const c = comps.find(x => String(x.position ?? x.Position) === pos);
        // No state = not in this match's squad: left out.
        return (c?.players || []).filter(p => p.state).map((p, idx) => toPlayer(p, idx, c)).sort((a, b) => a.role - b.role);
      };
      return { home: side('1'), away: side('2') };
    },
    // Yellow/red cards per player, from the livescore incidents (by id, or name).
    cardsByPlayer() {
      const byId = {};
      const byName = {};
      collectIncidents(this.ls).forEach(inc => {
        const t = typeOf(inc);
        if (t !== YELLOW_CARD && t !== RED_CARD) return;
        const { id, name } = incidentPlayer(inc);
        const add = (map, key) => {
          if (!key) return;
          const c = map[key] || (map[key] = { yellow: 0, red: false });
          if (t === RED_CARD) c.red = true; else c.yellow++;
        };
        if (id && id !== '-1') add(byId, id);
        add(byName, name.trim().toLowerCase());
      });
      return { byId, byName };
    },
    // Substitution incidents: player id -> seconds (the first one per player).
    subsByPlayer() {
      const map = {};
      collectIncidents(this.ls).forEach(inc => {
        if (typeOf(inc) !== SUBSTITUTION) return;
        const { id } = incidentPlayer(inc);
        if (!id || id === '-1') return;
        if (map[id] === undefined || inc.calculatedSeconds < map[id]) map[id] = inc.calculatedSeconds;
      });
      return map;
    },
    // Starting eleven, then the bench, home and away side by side row by row.
    sections() {
      const { home, away } = this.computedLineups;
      const shortName = name => {
        const parts = String(name).trim().split(/\s+/);
        return parts.length > 1 ? `${parts[0][0]} ${parts.slice(1).join(' ')}` : name;
      };
      // Goals only come with the player-stats API.
      const withShort = p => ({ ...p, short: shortName(p.name), card: this.cardOf(p), goals: p.goals || 0, ...this.subOf(p) });
      const zip = (h, a) => Array.from({ length: Math.max(h.length, a.length) }, (_, i) => ({ home: h[i] ? withShort(h[i]) : null, away: a[i] ? withShort(a[i]) : null }));
      const starters = zip(home.filter(p => p.starter), away.filter(p => p.starter));
      const bench = zip(home.filter(p => !p.starter), away.filter(p => !p.starter));
      const sections = [];
      // "Starting lineups" always heads the list; "Bench" only splits it when both exist.
      if (starters.length) sections.push({ key: 'starters', title: this.$t('statistics.startinglineups'), rows: starters });
      if (bench.length) sections.push({ key: 'bench', title: starters.length ? this.$t('statistics.bench') : this.$t('statistics.startinglineups'), rows: bench });
      return sections;
    },
    computedLineups() {
      const fromApi = this.apiLineups;
      if (fromApi.home.length || fromApi.away.length) return fromApi;
      const fromCompetitors = this.competitorLineups;
      if (fromCompetitors.home.length || fromCompetitors.away.length) return fromCompetitors;
      if (!this.isValidData) return { home: [], away: [] };

      const pStats = this.ls.PlayerStatistics || this.ls.playerStatistics;
      if (!pStats) return { home: [], away: [] };

      let home = [];
      let away = [];

      let homeId = null;
      let awayId = null;

      const parts = this.matchData.Fixture?.Participants || this.matchData.fixture?.participants || [];
      parts.forEach(p => {
        if (String(p.Position || p.position) === "1") homeId = String(p.Id || p.id);
        if (String(p.Position || p.position) === "2") awayId = String(p.Id || p.id);
      });

      if (!homeId && this.matchData.home_id) homeId = String(this.matchData.home_id);
      if (!awayId && this.matchData.away_id) awayId = String(this.matchData.away_id);

      pStats.forEach(p => {
        let playerObj = {
          id: p.Id || p.id || p.PlayerId || p.playerId || Math.random(),
          name: p.PlayerName || p.playerName || p.Name || p.name || 'Unknown',
          number: p.ShirtNumber || p.shirtNumber || p.JerseyNumber || p.jerseyNumber || '',
          // Livescore player stats carry no lineup flag: list them all as starters.
          starter: true
        };

        const teamIdStr = String(p.TeamId || p.teamId);
        const posStr = String(p.ParticipantPosition || p.participantPosition);

        if ((homeId && teamIdStr === homeId) || posStr === "1") {
          home.push(playerObj);
        } else if ((awayId && teamIdStr === awayId) || posStr === "2") {
          away.push(playerObj);
        }
      });

      return { home, away };
    }
  }
}
</script>

<style scoped>
.lineups {
  padding: 8px 12px 12px;
  color: var(--text-platform);
}

.lineups__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 32px;
  row-gap: 2px;
}

.lineups__player {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  min-height: 24px;
  font-size: var(--font-size-2xl);
}

.lineups__player--home { justify-content: flex-end; text-align: right; }
.lineups__player--away { justify-content: flex-start; }

.lineups__name { min-width: 0; }

.player-logo {
  flex: none;
  width: 14px;
  height: 14px;
  object-fit: contain;
}

.lineups__card,
.lineups__card ::v-deep svg {
  flex: none;
  display: block;
  width: 12px;
  height: 12px;
}

.lineups__sub {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 1px;
}

.lineups__sub-icon,
.lineups__sub-icon ::v-deep svg {
  display: block;
  width: 12px;
  height: 12px;
}

.lineups__sub-minute {
  font-size: var(--font-size-md);
  color: var(--text-op-50-platform);
}

.lineups__goals {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 1px;
}

.lineups__ball {
  display: block;
  width: 12px;
  height: 12px;
  object-fit: contain;
}

.lineups__goals-count {
  font-size: var(--font-size-md);
  font-weight: 700;
}

.captain-badge {
  flex: none;
  font-size: var(--font-size-md);
  font-weight: 700;
  line-height: 1;
  padding: 2px 4px;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
}

.lineups__separator {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 12px 0 8px;
  font-size: var(--font-size-2xl);
  font-weight: 700;
  white-space: nowrap;
}

.lineups__separator:first-child {
  margin-top: 0;
}

.lineups__separator::before,
.lineups__separator::after {
  content: '';
  flex: 1;
  height: 1px;
  background: rgba(255, 255, 255, 0.12);
}

.empty-state { display: flex; flex-direction: column; align-items: center; padding: 40px 0; font-size: var(--font-size-2xl); color: var(--text-op-50-platform) }
</style>
